'use client';

import { FC, useState } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateInvoiceSchema } from '@/schemas';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SelectClient } from '@/features/invoices/select-client';
import { CreateClientPopup } from '@/features/clients/create/create-client-popup';
import { FormError } from '@/components/form';
import { IInvoice } from '@/types/invoice';
import { editInvoice } from '@/actions';
import { calculateInvoiceTotals } from '@/lib/helpers/invoice-math';
import { getCurrencyFormatter } from '@/lib/currency-formatter';
import { Items } from '../items';
import { InvoiceType } from '../invoice-type';
import { DatePickerWithRange } from '../date-range';
import { useInvoiceContext } from '@/contexts/invoice-context';
import { useClientContext } from '@/contexts/client-context';
import { LoadingButton } from '@/components/loading-button';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { useSession } from 'next-auth/react';
import { toast } from 'sonner';
import { SingleDatePicker } from '../single-date-picker';
import { RecurringFrequencySelect } from '../recurring-frequency-select';
import { Switch } from '@/components/ui/switch';

interface IEditInvoice {
  invoice: IInvoice;
}

export const EditInvoiceForm: FC<IEditInvoice> = ({ invoice }) => {
  const [error, setError] = useState<string | undefined>('');
  const { status, wrap } = useSubmitStatus();

  const [items, setItems] = useState(invoice.items);
  const common = useTranslations('Common');

  const form = useForm<z.infer<typeof CreateInvoiceSchema>>({
    resolver: zodResolver(CreateInvoiceSchema),
    defaultValues: {
      clientId: invoice.clientId,
      invoiceNumber: invoice.invoiceNumber,
      invoiceType: invoice.invoiceType,
      status: invoice.status,
      serviceDate: {
        from: new Date(invoice.serviceFrom),
        to: invoice.serviceTo
          ? new Date(invoice.serviceTo)
          : new Date(invoice.serviceFrom),
      },
      dueDate: new Date(invoice.dueDate),
      issuedDate: new Date(invoice.issuedDate),

      items: invoice.items,
      ddvAdditionalInput: invoice.ddvAdditionalInput ?? '',
      isRecurring: invoice.isRecurring ?? false,
      recurringFrequency:
        (invoice.recurringFrequency as
          | 'WEEKLY'
          | 'MONTHLY'
          | 'QUARTERLY'
          | 'ANNUALLY') ?? 'MONTHLY',
      recurringNextDate: invoice.recurringNextDate
        ? new Date(invoice.recurringNextDate)
        : undefined,
      recurringEndDate: invoice.recurringEndDate
        ? new Date(invoice.recurringEndDate)
        : null,
    },
  });

  const isRecurring = form.watch('isRecurring');
  const canEditRecurring = !invoice.sourceRecurringId;

  const onSubmit = (values: z.infer<typeof CreateInvoiceSchema>) => {
    setError('');

    wrap(
      () =>
        new Promise<void>((resolve, reject) => {
          editInvoice({
            id: invoice.id,
            clientId: values.clientId,
            invoiceNumber: values.invoiceNumber,
            invoiceType: values.invoiceType,
            status: invoice.status,
            serviceDate: values.serviceDate,
            dueDate: values.dueDate,
            issuedDate: values.issuedDate,
            items,
            ddvAdditionalInput: values.ddvAdditionalInput,
            isRecurring: canEditRecurring ? values.isRecurring : undefined,
            recurringFrequency:
              canEditRecurring && values.isRecurring
                ? values.recurringFrequency
                : undefined,
            recurringNextDate:
              canEditRecurring && values.isRecurring
                ? values.recurringNextDate
                : undefined,
            recurringEndDate:
              canEditRecurring && values.isRecurring
                ? (values.recurringEndDate ?? null)
                : null,
          })
            .then((data) => {
              if (data.success) {
                toast.success(data.success);
                resolve();

                setTimeout(() => {
                  dispatch({ type: 'CLOSE_EDIT_INVOICE_POPUP' });
                  form.reset();
                }, 1500);
              } else {
                const errorMessage = data.error || common('somethingWentWrong');
                toast.error(errorMessage);
                setError(errorMessage);
                reject();
              }
            })
            .catch(() => {
              const errorMessage = common('somethingWentWrong');
              toast.error(errorMessage);
              setError(errorMessage);
              reject();
            });
        })
    );
  };

  const {
    subtotal: subTotalItemsSum,
    tax: subTotalTaxSum,
    total: totalSum,
  } = calculateInvoiceTotals(items);

  const { dispatch } = useInvoiceContext();
  const { state: client } = useClientContext();

  const t = useTranslations('CreateInvoicePopup');
  const session = useSession();

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <div className="flex justify-between gap-2 flex-col sm:flex-row">
              <FormField
                control={form.control}
                name="invoiceType"
                render={({ field }) => (
                  <FormItem className="flex flex-col w-full sm:w-1/2">
                    <FormLabel>{t('type')}</FormLabel>

                    <FormControl>
                      <InvoiceType
                        placeholder={t('selectType')}
                        value={field.value}
                        onSelect={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="invoiceNumber"
                render={({ field }) => (
                  <FormItem className="flex flex-col w-full sm:w-1/2">
                    <FormLabel>{t('invoiceNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} type="string" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-b"></div>

            <FormField
              control={form.control}
              name="serviceDate"
              render={({ field }) => (
                <FormItem className="flex flex-col w-full ">
                  <FormLabel>{t('dateOfServicePerformed')}</FormLabel>

                  <FormControl>
                    <DatePickerWithRange
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between gap-2 flex-col sm:flex-row">
              <FormField
                control={form.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col w-full sm:w-1/2">
                    <FormLabel>{t('dueDate')}</FormLabel>
                    <FormControl>
                      <SingleDatePicker
                        id="due-date"
                        ariaLabel="Due date"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="issuedDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col w-full sm:w-1/2">
                    <FormLabel>{t('issuedDate')}</FormLabel>
                    <FormControl>
                      <SingleDatePicker
                        id="issued-date"
                        ariaLabel="Issued date"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-b"></div>

            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('selectClient')}</FormLabel>
                  <FormControl>
                    <SelectClient
                      placeholder={t('selectClient')}
                      value={field.value}
                      onSelect={(value) => {
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {canEditRecurring && (
              <>
                <div className="border-b"></div>
                <div className="flex flex-col gap-4 p-3 border rounded-lg bg-muted/30">
                  <FormField
                    control={form.control}
                    name="isRecurring"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between">
                        <FormLabel>{t('recurring')}</FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={status === 'loading'}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  {isRecurring && (
                    <>
                      <FormField
                        control={form.control}
                        name="recurringFrequency"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('recurringFrequency')}</FormLabel>
                            <FormControl>
                              <RecurringFrequencySelect
                                value={
                                  (field.value as
                                    | 'WEEKLY'
                                    | 'MONTHLY'
                                    | 'QUARTERLY'
                                    | 'ANNUALLY') ?? 'MONTHLY'
                                }
                                onChange={field.onChange}
                                disabled={status === 'loading'}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="recurringNextDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('recurringNextDate')}</FormLabel>
                            <FormControl>
                              <SingleDatePicker
                                id="edit-recurring-next-date"
                                ariaLabel="Next recurring date"
                                value={field.value ?? null}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="recurringEndDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('recurringEndDate')}</FormLabel>
                            <FormControl>
                              <SingleDatePicker
                                id="edit-recurring-end-date"
                                ariaLabel="Recurring end date (optional)"
                                value={field.value ?? null}
                                onChange={(d) => field.onChange(d ?? null)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </div>
              </>
            )}

            <div className="border-b"></div>

            <Items
              status={status}
              items={items}
              setItems={setItems}
              currency={session.data?.user.currency}
            />

            <div className="border-b"></div>

            <FormField
              control={form.control}
              name="ddvAdditionalInput"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ddvAdditionalInputLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={status === 'loading'}
                      placeholder={t('ddvAdditionalInputPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="p-3">
              <div className="flex justify-between">
                <h6>{t('subTotal')}</h6>

                <span>
                  {getCurrencyFormatter(
                    subTotalItemsSum,
                    session.data?.user.currency
                  )}
                </span>
              </div>

              <div className="flex justify-between">
                <h6>{t('tax')}:</h6>

                <span>
                  {getCurrencyFormatter(
                    subTotalTaxSum,
                    session.data?.user.currency
                  )}
                </span>
              </div>

              <div className="flex justify-between">
                <h6>{t('total')}:</h6>

                <span>
                  {getCurrencyFormatter(totalSum, session.data?.user.currency)}
                </span>
              </div>
            </div>
          </div>

          <FormError message={error} />

          <LoadingButton
            isFull={true}
            type="submit"
            status={status}
            idleText={common('confirm')}
          />
        </form>
      </Form>

      {client.isCreateClientPopupOpen && <CreateClientPopup />}
    </>
  );
};
